import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { BlogPost } from '../types';

interface FeaturedPostProps {
  post: BlogPost;
}

const FeaturedPost = ({ post }: FeaturedPostProps) => {
  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden shadow-lg hover:shadow-xl transition-all duration-fast">
      <div className="grid lg:grid-cols-2 gap-0">
        <div className="relative h-64 lg:h-full overflow-hidden">
          <Image
            src={post.featuredImage}
            alt={post.featuredImageAlt}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute top-4 left-4">
              <span className="inline-flex items-center space-x-1 bg-accent text-accent-foreground px-3 py-1.5 rounded-full text-xs font-semibold">
              <Icon name="Star" className="w-3 h-3" />
              <span>Featured</span>
            </span>
          </div>
        </div>

        <div className="p-8 lg:p-12 flex flex-col justify-center">
          <div className="flex items-center space-x-4 mb-4">
            <span 
              className="px-3 py-1 rounded-full text-xs font-semibold"
              style={{ 
                backgroundColor: `${post.category.color}20`,
                color: post.category.color 
              }}
            >
              {post.category.name}
            </span>
              <div className="flex items-center space-x-2 text-sm text-text-secondary">
              <Icon name="Clock" className="w-3.5 h-3.5" />
              <span>{post.readTime} min read</span>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-text-primary mb-4 hover:text-primary transition-colors duration-fast">
            <Link to={`/blog/${post.slug}`}>{post.title}</Link>
          </h2>

          <p className="text-text-secondary mb-6 line-clamp-3">
            {post.excerpt}
          </p>

          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <Image
                src={post.author.avatar}
                alt={post.author.avatarAlt}
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <p className="text-sm font-medium text-text-primary">{post.author.name}</p>
                <p className="text-xs text-text-secondary">{post.author.role}</p>
              </div>
            </div>
            <p className="text-sm text-text-secondary">
              {post.publishedAt.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </p>
          </div>

          <div className="flex items-center space-x-4">
            <Link to={`/blog/${post.slug}`}>
              <Button variant="default" iconName="ArrowRight" iconPosition="right">
                Read Full Article
              </Button>
            </Link>
            <div className="flex items-center space-x-4 text-sm text-text-secondary">
              <div className="flex items-center space-x-1">
                <Icon name="Eye" className="w-4 h-4" />
                <span>{post.views.toLocaleString()}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Heart" className="w-4 h-4" />
                <span>{post.likes}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedPost;